/**
 * "Close Revit 2025 first" (#189) — shown when a whole-tree install, update, repair or
 * rollback is clicked while that year's CAD host is open.
 *
 * The provisioner refuses such a write anyway, but only after the click: the refusal came
 * back as a toast the user could miss, and a Revit left open keeps the old build loaded. So
 * the pane asks first. Continue re-checks: the write starts only once the host is actually
 * closed, and a host still open says so here rather than failing the install.
 *
 * A command-set patch never comes here — it is applied to a running host by design.
 */
import { useLocale } from "../../i18n/LocaleProvider";
import { formatYearRange } from "../../lib/plugins";
import { Modal } from "../settings/Modal";

export interface CloseHostPrompt {
  host: string;
  years: string[];
  /** Continue was chosen and the host was still open. */
  stillOpen: boolean;
  /** A re-check is in flight. */
  checking: boolean;
}

export function CloseHostDialog({
  prompt,
  onContinue,
  onCancel,
}: {
  prompt: CloseHostPrompt | null;
  onContinue: () => void;
  onCancel: () => void;
}) {
  const { t } = useLocale();
  const host = prompt?.host ?? "";
  const years = formatYearRange(prompt?.years ?? []);
  return (
    <Modal
      open={prompt !== null}
      onClose={onCancel}
      closable={!prompt?.checking}
      title={t("plugins.closeHost.title", { host, years })}
      footer={
        <>
          <button
            type="button"
            className="settings-btn secondary"
            onClick={onCancel}
            disabled={prompt?.checking}
          >
            {t("common.cancel")}
          </button>
          <button type="button" className="settings-btn" onClick={onContinue} disabled={prompt?.checking}>
            {prompt?.checking ? t("plugins.closeHost.checking") : t("plugins.closeHost.continue")}
          </button>
        </>
      }
    >
      <p className="settings-note">{t("plugins.closeHost.body", { host, years })}</p>
      {prompt?.stillOpen ? (
        <p className="settings-msg err" role="alert">
          {t("plugins.closeHost.stillOpen", { host, years })}
        </p>
      ) : null}
    </Modal>
  );
}
